import { useTranslation } from 'react-i18next';
import type { VenueSummary } from '../hooks/useVenue.js';
import { addServiceDays, currentServiceDate, formatDate, formatHijri } from '../lib/time.js';

/**
 * Service-date navigation, with both calendars.
 *
 * The Hijri date sits alongside the Gregorian one everywhere a date is chosen,
 * because during Ramadan the staff conversation is "the fifteenth" meaning the
 * fifteenth of Ramadan, and a system that only speaks Gregorian makes everyone
 * translate in their head.
 *
 * The arrows are `‹` and `›` and DO NOT swap in RTL: they are placed with
 * logical properties, so "previous" is already on the correct side of the
 * control, and swapping the glyphs as well would move it back.
 */
export function ServiceDatePicker({
  value,
  onChange,
  venue,
}: {
  value: string;
  onChange: (next: string) => void;
  venue: VenueSummary;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const today = currentServiceDate(venue);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center rounded-md border border-slate-200">
        <button
          type="button"
          aria-label={t('common.date')}
          className="px-2 py-1 text-slate-600 hover:bg-brand-teal-light"
          onClick={() => onChange(addServiceDays(value, -1))}
        >
          {'‹' /* i18n-ok: a chevron, placed logically */}
        </button>
        <input
          type="date"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="border-0 bg-transparent px-2 py-1 font-mono text-sm"
          aria-label={t('common.date')}
        />
        <button
          type="button"
          aria-label={t('common.date')}
          className="px-2 py-1 text-slate-600 hover:bg-brand-teal-light"
          onClick={() => onChange(addServiceDays(value, 1))}
        >
          {'›' /* i18n-ok: a chevron, placed logically */}
        </button>
      </div>

      {value !== today ? (
        <button
          type="button"
          onClick={() => onChange(today)}
          className="rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-600 hover:bg-brand-teal-light"
        >
          {t('common.today')}
        </button>
      ) : null}

      <span className="text-sm text-slate-600">{formatDate(value, venue, locale)}</span>
      <span className="text-sm text-brand-bronze">{formatHijri(value, venue, locale)}</span>
    </div>
  );
}
